<template>
  <div class="c-third-supplier-product-report">
    <!-- 筛选条件（对齐订单详情的 c-info-card 分区） -->
    <c-info-card id="thirdSupplierFilter" class="basic-info" title="筛选条件">
      <el-form :model="searchForm" label-width="110px" class="report__filter">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="当前供应商">
              <el-input :model-value="supplierDisplay" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12" class="report__filter-actions">
            <el-button type="primary" :disabled="!supplierInfo.supplierCode" :loading="loading" @click="handleSearch">查询</el-button>
            <el-button :disabled="!supplierInfo.supplierCode" @click="handleReset">重置</el-button>
            <el-button :loading="exporting" :disabled="!supplierInfo.supplierCode" @click="handleExport">导出按日明细</el-button>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="统计时间" prop="statRangePreset">
              <el-radio-group v-model="searchForm.statRangePreset" @change="onPresetChange">
                <el-radio-button label="LAST_7">近 7 天</el-radio-button>
                <el-radio-button label="LAST_30">近 30 天</el-radio-button>
                <el-radio-button label="CUSTOM">自定义</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="自定义区间">
              <el-date-picker
                v-model="dateRangeModel"
                type="daterange"
                range-separator="至"
                start-placeholder="开始"
                end-placeholder="结束"
                value-format="YYYY-MM-DD"
                :disabled="searchForm.statRangePreset !== 'CUSTOM'"
                :disabled-date="disabledStatDate"
                style="width: 100%"
                @change="onDateRangeChange"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </c-info-card>

    <c-info-card
      id="thirdSupplierSummary"
      class="basic-info"
      title="区间汇总"
      :tip="METRICS_HELP_TEXT"
      effect="light"
      :raw-content="true"
    >
      <!--      <div class="report__summary-head">-->
      <!--        <el-popover placement="bottom-end" :width="520" trigger="hover">-->
      <!--          <template #reference>-->
      <!--            <span class="report__summary-help">-->
      <!--              <span>指标说明</span>-->
      <!--              <el-icon class="report__summary-help-icon"><QuestionFilled /></el-icon>-->
      <!--            </span>-->
      <!--          </template>-->
      <!--          <div class="report__summary-help-body">{{ METRICS_HELP_TEXT }}</div>-->
      <!--        </el-popover>-->
      <!--      </div>-->
      <el-row :gutter="10" class="report__summary-cards">
        <el-col v-for="(c, idx) in summaryCardItems" :key="idx" :xs="24" :sm="12" :md="8">
          <el-card shadow="never" class="report__card">
            <div class="report__card-title">{{ c.title }}</div>
            <div class="report__card-value">{{ c.value }}</div>
            <div class="report__card-sub">{{ c.sub }}</div>
          </el-card>
        </el-col>
      </el-row>
    </c-info-card>

    <c-info-card id="thirdSupplierDaily" class="basic-info" title="按日明细">
      <div v-loading="loading" class="report__table-wrap">
        <el-table :data="tableRows" border stripe style="width: 100%">
          <template v-for="col in DailyTableColumns" :key="col.prop">
            <el-table-column v-if="col.formatter" :label="col.label" :min-width="col.minWidth">
              <template #default="scope">
                {{ col.formatter(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column v-else :label="col.label" :min-width="col.minWidth">
              <template #default="scope">
                {{ scope.row[col.prop] ?? '--' }}
              </template>
            </el-table-column>
          </template>
        </el-table>
        <el-pagination
          class="report__pager"
          background
          layout="prev, pager, next, sizes, ->, total"
          :page-sizes="[20, 30, 50]"
          :total="total"
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          @size-change="handleSearch"
          @current-change="loadTable"
        />
      </div>
    </c-info-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { QuestionFilled } from '@element-plus/icons'
import { useApiRequest, useCurrentAppInstance } from '@zto/zpage'
import { UI_PAGE_EVENTS, appUtil } from '@zto/zpage-ui-element'
import dayjs from 'dayjs'
import {
  defaultSearchModel,
  buildRangePayload,
  daysInclusive,
  buildSummaryCards,
  METRICS_HELP_TEXT,
  DailyTableColumns,
  type StatSearchModel
} from './config'
import { ElLoading } from 'element-plus'

const apiRequest = useApiRequest()
const app = useCurrentAppInstance()

const { Message } = app.useMessage()

const searchForm = reactive<StatSearchModel>(defaultSearchModel())
const dateRangeModel = ref<[string, string] | null>([searchForm.statDateStart, searchForm.statDateEnd])
const supplierInfo = ref<any>({})

const supplierDisplay = computed(() => {
  // const basic = app.stores.userStore?.data?.basic || {}
  // const name = basic.supplierOrgName || ''
  // const code = basic.supplierOrgCode || ''
  const { supplierCode, supplierName } = supplierInfo.value
  return `${supplierName || ''}（${supplierCode || ''}）`
})

const summary = ref<Record<string, any>>({})
const summaryCardItems = computed(() => buildSummaryCards(summary.value))

const tableRows = ref<any[]>([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)
const loading = ref(false)
const exporting = ref(false)

function disabledStatDate(d: Date) {
  return dayjs(d).startOf('day').isAfter(yesterdayStart(), 'day')
}

function yesterdayStart() {
  return dayjs().subtract(1, 'day').startOf('day')
}

function onPresetChange() {
  if (searchForm.statRangePreset !== 'CUSTOM') {
    const r = buildRangePayload(searchForm)
    searchForm.statDateStart = r.statDateStart
    searchForm.statDateEnd = r.statDateEnd
    dateRangeModel.value = [r.statDateStart, r.statDateEnd]
  }
}

function onDateRangeChange(val: [string, string] | null) {
  if (val && val.length === 2) {
    searchForm.statDateStart = val[0]
    searchForm.statDateEnd = val[1]
  }
}

watch(
  () => [searchForm.statDateStart, searchForm.statDateEnd],
  ([a, b]) => {
    if (a && b) dateRangeModel.value = [a, b]
  }
)

function validateRange(): boolean {
  const preset = searchForm.statRangePreset
  if (preset !== 'CUSTOM') return true
  const start = searchForm.statDateStart
  const end = searchForm.statDateEnd
  if (!start || !end) {
    Message.warning('请选择统计时间起止')
    return false
  }
  if (dayjs(start).isAfter(dayjs(end))) {
    Message.warning('统计开始日不能晚于结束日')
    return false
  }
  if (daysInclusive(start, end) > 183) {
    Message.warning('时间范围最长为半年（183 天）')
    return false
  }
  if (!dayjs(end).isBefore(dayjs().startOf('day'))) {
    Message.warning('结束日不可选择今天及未来日期')
    return false
  }
  return true
}

function handleSearch() {
  currentPage.value = 1
  initData()
}

function handleReset() {
  const d = defaultSearchModel()
  searchForm.statRangePreset = d.statRangePreset
  searchForm.statDateStart = d.statDateStart
  searchForm.statDateEnd = d.statDateEnd
  dateRangeModel.value = [d.statDateStart, d.statDateEnd]
  currentPage.value = 1
  initData()
}

/**
 * 获取供应商信息
 */
async function getSupplierInfo() {
  try {
    const res = await apiRequest({
      action: '/cabinet/admin/oms/supplierReport/getSupplierConfig',
      params: {}
    })
    supplierInfo.value = res || {}
  } catch (e) {
    console.log('获取供应商信息', e)
  }
}

/**
 * 获取统计汇总
 */
async function getStatisticalSummary() {
  try {
    if (!validateRange()) return
    const { supplierCode } = supplierInfo.value
    if(!supplierCode) return
    const range = buildRangePayload(searchForm)
    const res = await apiRequest({
      action: '/cabinet/admin/oms/supplierReport/sum',
      params: {
        ...range,
        supplierCode
      }
    })
    summary.value = res || {}
  } catch (error) {
    console.info('🚀 ~ CThirdSupplierProductReport getStatisticalSummary error', error)
    summary.value = {}
  }
}

/**
 * 获取列表数据
 */
async function loadTable() {
  try {
    if (!validateRange()) return
    const { supplierCode } = supplierInfo.value
    if(!supplierCode) return
    const range = buildRangePayload(searchForm)
    const res = await apiRequest({
      action: '/cabinet/admin/oms/supplierReport/page',
      params: {
        ...range,
        supplierCode,
        pageIndex: currentPage.value,
        pageSize: pageSize.value
      }
    })
    const { data, total: totalCount } = res || {}
    tableRows.value = data || []
    total.value = totalCount || 0
  } catch (error) {
    console.info('🚀 ~ CThirdSupplierProductReport loadTable error', error)
    tableRows.value = []
    total.value = 0
  }
}
const initData = async () => {
  try {
    loading.value = true
    const { supplierCode } = supplierInfo.value
    if (!supplierCode) {
      await getSupplierInfo()
    }
    await Promise.all([getStatisticalSummary(), loadTable()])
    loading.value = false
  } catch (error) {
    console.log('CThirdSupplierProductReport initData error', error)
    loading.value = false
  }
}

/**
 * 导出列表数据
 */
async function handleExport() {
  if (!validateRange()) return
  const { supplierCode } = supplierInfo.value
  // 显示全屏loading
  const loadingInstance = ElLoading.service({
    lock: true,
    text: '正在导出数据，请稍候...',
    background: 'rgba(0, 0, 0, 0.7)'
  })

  try {
    const size = 500 // 每页大小
    let current = 1 // 从第1页开始
    let lastId = null
    let allData: any[] = [] // 存储所有数据
    let hasMoreData = true // 是否还有更多数据
    const range = buildRangePayload(searchForm)

    // 循环查询直到数据为空或报错
    while (hasMoreData) {
      try {
        const res: any = await app.request({
          url: '/cabinet/admin/oms/supplierReport/export',
          data: {
            ...range,
            supplierCode,
            current: current,
            size: size,
            lastId: lastId
          }
        })

        const pageData: any[] = res.list || []
        lastId = res.lastId || null

        // 如果当前页没有数据，说明已经查询完所有数据
        if (!pageData || pageData.length === 0) {
          hasMoreData = false
          break
        }

        // 将当前页数据添加到总数据中
        allData = allData.concat(pageData)

        if (pageData.length < size) {
          hasMoreData = false
          break
        }

        // 继续查询下一页
        current++

        // 更新提示信息
        // Message.info(`已查询 ${allData.length} 条数据，继续查询第 ${current} 页...`)
      } catch (pageError: any) {
        // 如果查询出错，停止查询
        console.error(`查询第 ${current} 页时出错:`, pageError)
        hasMoreData = false

        // 如果已经有数据，继续导出；如果没有数据，提示错误
        if (allData.length === 0) {
          throw pageError
        }
        break
      }
    }

    // 4. 检查是否有数据可导出
    if (!allData || allData.length === 0) {
      Message.warning('没有可导出的数据')
      return
    }

    // 6. 格式化数据（应用 formatter）
    const formattedData = allData.map((row: any) => {
      const formattedRow: any = {}
      DailyTableColumns.forEach((col: any) => {
        if (col.formatter) {
          formattedRow[col.prop] = col.formatter(row)
        } else {
          formattedRow[col.prop] = row[col.prop] ?? '--'
        }
      })
      return formattedRow
    })

    const exportDataFn = app.useComponentsConfig('xlsx.exportData')

    if (exportDataFn) {
      exportDataFn(formattedData, {
        columns: DailyTableColumns,
        fileName: '三方对接供应商报表',
        app
      })
    }
    Message.success(`导出成功，共导出 ${formattedData.length} 条数据`)
  } catch (error) {
    console.error('导出失败:', error)
  } finally {
    // 关闭全屏loading
    loadingInstance.close()
  }
}

let activeEvent: any = undefined

onMounted(() => {
  initData()
  activeEvent = app.ons(UI_PAGE_EVENTS.ACTIVATED, initData)
})

onBeforeUnmount(() => {
  activeEvent && app.offs(activeEvent.eventTypes, activeEvent.handler)
})
</script>

<style lang="scss" scoped>
.c-third-supplier-product-report {
  padding: 15px;
  background: #fff;
  box-sizing: border-box;
  min-height: 100%;
}

.basic-info {
  margin-bottom: 16px;
}

.report__filter {
  padding-top: 4px;
}

.report__filter-actions {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.report__summary-head {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.report__summary-help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--el-color-primary);
  cursor: pointer;
}

.report__summary-help-icon {
  font-size: 16px;
}

.report__summary-help-body {
  font-size: 13px;
  line-height: 1.6;
  color: var(--el-text-color-regular);
  white-space: pre-wrap;
  max-height: 360px;
  overflow: auto;
}

.report__summary-cards {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.report__card {
  margin-bottom: 10px;
  min-height: 100px;
}

.report__card-title {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  margin-bottom: 6px;
}

.report__card-value {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 8px;
}

.report__card-sub {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.report__table-wrap {
  padding: 4px 0 0;
}

.report__pager {
  margin-top: 12px;
  justify-content: flex-end;
}
</style>

<style>
.c-third-supplier-product-report .el-input.is-disabled .el-input__inner {
  color: inherit;
}
</style>
